import React, {useEffect, useState} from 'react';
import {useIntl} from "react-intl";
import {HttpStatus} from "../../../../../modules/common/enums/HttpStatus";
import {DepartmentDto} from "../../../../../modules/account/dto/DepartmentDto";
import {DepartmentApi} from "../../../../../modules/account/api/DepartmentApi";
import {MessageUtils} from "../../../../../utils/MessageUtils";
import {Dialog, DialogActions, DialogContent, DialogTitle,} from "@mui/material";
import FormFieldText from "../../../../../components/FormFieldText";
import CancelButton from "../../../../../components/buttons/CancelButton";
import SaveButton from "../../../../../components/buttons/SaveButton";
import DepartmentSelectDialog from "./DepartmentSelectDialog";
import SearchIconButton from '../../../../../components/buttons/SearchIconButton';
import CloseIconButton from "../../../../../components/buttons/CloseIconButton";

export type DepartmentDetailDialogProps = {
    open: boolean;
    department: DepartmentDto;
    departments: Array<DepartmentDto>;
    departmentMap: Map<string, DepartmentDto>;
    onClose: () => void;
    onOk: () => void;
};

const DepartmentDetailDialog: React.FC<DepartmentDetailDialogProps> = (props) => {
    const intl = useIntl();
    const title = props.department.id ? 'ACCOUNT_DEPARTMENT_MODIFY' : 'ACCOUNT_DEPARTMENT_CREATE';

    const [innerDepartment, setInnerDepartment] = useState<DepartmentDto>(props.department);

    const [openSelectParentDialog, setOpenSelectParentDialog] = useState(false);

    const [messageMap, setMessageMap] = useState(new Map<string, string>());

    useEffect(() => {
        if (props.open) {
            setInnerDepartment({...props.department});
            setMessageMap(new Map<string, string>());
        }
    }, [props.open]);

    const _handleChange = (name: string, value: any) => {
        setInnerDepartment(previousDepartment => ({...previousDepartment, [name]: value}));
    };

    const _handleOk = async () => {
        if (_validate()) {
            if (props.department.id) {
                const responseResult = await DepartmentApi.updateDepartment(innerDepartment);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'ACCOUNT_DEPARTMENT_MODIFY_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            } else {
                const responseResult = await DepartmentApi.createDepartment(innerDepartment);
                if (responseResult.status === HttpStatus.SUCCESS) {
                    MessageUtils.success(intl.formatMessage({id: 'ACCOUNT_DEPARTMENT_CREATE_SUCCESS'}));
                    props.onClose();
                    props.onOk();
                }
            }
        }
    };

    const _handleCancel = () => {
        props.onClose();
    };

    const _validate = () => {
        const newMessageMap = new Map<string, string>();
        if (innerDepartment.code === '') {
            newMessageMap.set('code', intl.formatMessage({id: 'ACCOUNT_DEPARTMENT_CODE_REQUIRED'}));
        }
        if (innerDepartment.name === '') {
            newMessageMap.set('name', intl.formatMessage({id: 'ACCOUNT_DEPARTMENT_NAME_REQUIRED'}));
        }

        setMessageMap(newMessageMap);
        return newMessageMap.size <= 0;
    };

    return (
        <Dialog open={props.open} onClose={_handleCancel} fullWidth={true}>
            <DialogTitle>
                {intl.formatMessage({id: title})}
            </DialogTitle>
            <DialogContent>
                <DepartmentSelectDialog departments={props.departments} open={openSelectParentDialog}
                                        onSelect={(value) => _handleChange("parentId", value)}
                                        onClose={() => setOpenSelectParentDialog(false)}/>
                <FormFieldText label={intl.formatMessage({id: "ACCOUNT_DEPARTMENT_PARENT"})}
                               value={innerDepartment.parentId && props.departmentMap.has(innerDepartment.parentId) ? props.departmentMap.get(innerDepartment.parentId)?.name : ''}
                               disabled={true}
                               endAdornment={
                                   <div>
                                       <SearchIconButton onClick={() => setOpenSelectParentDialog(true)}/>
                                       <CloseIconButton onClick={() => _handleChange("parentId", "")}/>
                                   </div>
                               }/>
                <FormFieldText label={intl.formatMessage({id: "ACCOUNT_DEPARTMENT_CODE"})}
                               required={true} errorText={messageMap.get("code")}
                               value={innerDepartment.code}
                               onChange={(value) => {
                                   _handleChange("code", value)
                               }}/>
                <FormFieldText label={intl.formatMessage({id: "ACCOUNT_DEPARTMENT_NAME"})}
                               required={true} errorText={messageMap.get("name")}
                               value={innerDepartment.name}
                               onChange={(value) => {
                                   _handleChange("name", value)
                               }}/>
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={_handleCancel}/>
                <SaveButton onClick={_handleOk}/>
            </DialogActions>
        </Dialog>
    );
};

export default DepartmentDetailDialog;
